import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../services/firebase';
import { 
  onAuthStateChanged, 
  signOut, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { authAPI } from '../services/api';
import { ROLES } from '../config/constants';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchUserData = async (user) => {
    try {
      // Force token refresh to get fresh claims
      await user.getIdToken(true);
      const tokenResult = await user.getIdTokenResult();
      const claims = tokenResult.claims;
      
      // Get user profile from backend
      const backendUser = await authAPI.getCurrentUser();
      
      const userData = {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        photoURL: user.photoURL,
        role: claims.role || ROLES.USER,
        isSuperAdmin: claims.role === ROLES.SUPER_ADMIN,
        ...backendUser,
        hasAppliedToAcademy: claims.hasAppliedToAcademy || false
      };
      
      setCurrentUser(userData);
    } catch (err) {
      setError(err.message || 'Failed to load user data');
      setCurrentUser(null);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;

      setLoading(true);
      if (user) {
        await fetchUserData(user);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
      setIsInitialized(true);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      setLoading(true);
      await signOut(auth);
      setCurrentUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (!user.emailVerified) {
        await sendEmailVerification(user);
        console.log('Verification email sent to: ', user.email);
        await signOut(auth);
        throw new Error('A verification email has been sent to your address. Please check your inbox and follow the link to verify your account.');
      }
      
      return user;
    } catch (err) {
      let errorMessage = 'An error occurred during sign in. Please try again.';
      
      // Provide more specific messages for common auth errors
      if (err.code === 'auth/invalid-credential' || 
          err.code === 'auth/invalid-login-credentials' || 
          err.code === 'auth/user-not-found' || 
          err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many unsuccessful login attempts. Please try again later or reset your password.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message) {
        // Use the error message if it's a custom one (like our email verification message)
        errorMessage = err.message;
      }
      
      // Create a friendly error object with the better message
      const friendlyError = {
        code: err.code || 'auth/unknown',
        message: errorMessage,
        originalError: err
      };
      
      setError(friendlyError.message);
      throw friendlyError;
    }
  };

  const register = async (email, password) => {
    try {
      setError(null);
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      let errorMessage = 'An error occurred during registration. Please try again.';
      
      // Provide more specific messages for common auth errors
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already associated with an account. Please use a different email or try signing in.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'The email address is not valid. Please enter a valid email address.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Your password is too weak. Please choose a stronger password.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Create a friendly error object with the better message
      const friendlyError = {
        code: err.code || 'auth/unknown',
        message: errorMessage,
        originalError: err
      };
      
      setError(friendlyError.message);
      throw friendlyError;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      let errorMessage = 'An error occurred while sending the password reset email. Please try again.';
      
      // Provide more specific messages for common auth errors
      if (err.code === 'auth/invalid-email') {
        errorMessage = 'The email address is not valid. Please enter a valid email address.';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address. Please check your email or create a new account.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Create a friendly error object with the better message
      const friendlyError = {
        code: err.code || 'auth/unknown',
        message: errorMessage,
        originalError: err
      };
      
      setError(friendlyError.message);
      throw friendlyError;
    }
  };

  const reloadUser = async () => {
    const user = auth.currentUser;
    if (user) {
      await fetchUserData(user);
    }
  };

  const getIdTokenResult = async () => {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdTokenResult(true); // force refresh for fresh claims
    }
  }
  
  // Role-checking helper
  const hasRole = (requiredRole) => {
    const role = currentUser?.role;

    if (!role) return false;

    // Example: 'superAdmin' inherits 'admin' privileges
    if (requiredRole === ROLES.ADMIN) {
      return role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;
    }

    return role === requiredRole;
  };

  const value = {
    currentUser,
    user: currentUser, // For backward compatibility with components using user
    loading,
    error,
    isInitialized,
    verified: currentUser?.emailVerified || false,
    signOut: handleSignOut,
    logout: handleSignOut, // For backward compatibility with components using logout
    reloadUser,
    hasRole,
    getIdTokenResult,
    login,
    register,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
